import { EnterpriseDepartmentUserType } from '@agent-ds/shared/interfaces';
import { forkJoin } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { PopupControlComponent } from '../../popup-control/popup-control.component';
import { MailPopupConfig, MailPopupType } from '../config';
import { StudentJobConfig } from './student-job-config';

export class ProgressRecommendConfig extends StudentJobConfig {
  public getTemplateId(): number {
    return 6;
  }

  public getTemplateTypeId(): number[] {
    return [4];
  }

  public getPopupConfig(): MailPopupConfig[] {
    return [
      { type: MailPopupType.JOB_CHECK, customFilter: { jobUserType: EnterpriseDepartmentUserType.RA }, title: '推薦依頼送信' },
      { type: MailPopupType.STUDENT, bodyArea: true, stepBack: 2, title: '推薦依頼送信', message: ' ' },
    ];
  }

  public setParams(params: any): void {
    if (this.sender) {
      this.sender.model.progressId = params.progressId;
      this.sender.model.progressType = params.progressType;
      this.sender.model.progressN = params.progressN;
      this.sender.model.progressSeminarType = params.progressSeminarType;
      this.sender.model.progressSeminarAt = params.progressSeminarAt;
      this.sender.model.progressSeminarAtMailSendFlag = params.progressSeminarAtMailSendFlag;
      this.sender.model.progressIsStudentDelay = params.progressIsStudentDelay;
      this.sender.model.progressHasInterview = params.progressHasInterview;
      this.sender.model.progressStatus = params.progressStatus;
    }
    super.setParams(params);
  }

  sendAfterFlow(): boolean {
    return true;
  }

  public initForm(): void {
    return;
  }

  public update(): void {
    return;
  }

  public save(toSave: any): void {
    toSave.jobIds = this.sender.model.jobs.map((j) => j.id);
    toSave.studentId = this.sender.model.students[0].id;
    this.sender.userService.getAll().subscribe((users) => {
      const raUsers = this.sender.model.jobs
        .map((j) =>
          j.jobUsers
            .filter((u) => u.type === EnterpriseDepartmentUserType.RA)
            .map((u) => (users.find((user) => user.id === u.userId) || { email: null }).email)
            .filter((m) => m),
        )
        .flatten();
      const paUsers = this.sender.model.jobs
        .map((j) =>
          j.jobUsers
            .filter((u) => u.type === EnterpriseDepartmentUserType.PA)
            .map((u) => (users.find((user) => user.id === u.userId) || { email: null }).email)
            .filter((m) => m),
        )
        .flatten();
      const asUsers = this.sender.model.jobs
        .map((j) =>
          j.jobUsers
            .filter((u) => u.type === EnterpriseDepartmentUserType.アシ)
            .map((u) => (users.find((user) => user.id === u.userId) || { email: null }).email)
            .filter((m) => m),
        )
        .flatten();
      toSave.to = raUsers.length ? raUsers : paUsers;
      toSave.cc = asUsers;
      if (this.sender.model.progressType === 1) {
        toSave.cc.push(this.sender.authService.loginUser.email);
      }
      if (this.sender.model.progressType == null) {
        // includeResumeCheck を渡すのはこの一括推薦のみ。他の checkProgress() 呼び出しは従来動作のまま
        this.sender.progressApiService.checkProgress(this.sender.model.students.map((s) => s.id), toSave.jobIds, true).subscribe((res) => {
          if (res && res.length) {
            // 「進捗重複」と「履歴書未提出」を区分して保持する。
            // type は includeResumeCheck=true のときだけ返る。無い場合（旧API互換）は進捗重複として扱う
            this.sender.progressErrorInfo = res.filter((info) => info.type !== 'resumeNotSubmitted');
            this.sender.progressResumeErrorInfo = res.filter((info) => info.type === 'resumeNotSubmitted');

            // 履歴書NGのペアは、どのボタンを選んでも進捗更新・メール送信の両方から除外する
            const resumeOkJobIdsFor = (studentId: number): number[] =>
              toSave.jobIds.filter(
                (jobId) => !this.sender.progressResumeErrorInfo.some((ng) => ng.studentId === studentId && ng.jobId === jobId),
              );
            // 履歴書NGに加えて進捗重複も除いたもの（「重複案件を除いて送信」用）
            const sendableJobIdsFor = (studentId: number): number[] =>
              resumeOkJobIdsFor(studentId).filter(
                (jobId) => !this.sender.progressErrorInfo.some((error) => error.studentId === studentId && error.jobId === jobId),
              );
            const resumeOkStudentsWithJobIds = {};
            const studentsWithJobIds = {};
            this.sender.model.students.forEach((student) => {
              resumeOkStudentsWithJobIds[student.id] = resumeOkJobIdsFor(student.id);
              studentsWithJobIds[student.id] = sendableJobIdsFor(student.id);
            });
            // メールは toSave.studentId（= students[0]）宛の1通のみ。進捗更新は選択された全求職者が対象なので、
            // 「メールを送れるか」と「進捗を更新できるか」を別々に判定しないと jobIds が空のまま送信してしまう
            const mailJobIds = resumeOkJobIdsFor(toSave.studentId);
            const mailJobIdsExcludingDuplicate = sendableJobIdsFor(toSave.studentId);
            const hasProgressTarget = (map: any): boolean =>
              this.sender.model.students.some((student) => map[student.id] && map[student.id].length);

            // ダイアログの状態は「進捗重複あり」「履歴書NGあり」の組み合わせで決まる。
            // 重複が無いのに「重複案件は〜」と表示しても意味が通らないため、文言とボタン構成の両方を出し分ける
            const hasDuplicate = this.sender.progressErrorInfo.length > 0;
            const hasResumeNg = this.sender.progressResumeErrorInfo.length > 0;

            // 進捗は一切作らず、送れる求人のみメール送信する
            const mailOnlyButton = (confirmText: string) => ({
              confirmText: confirmText,
              // メール送信しか行わないボタンなので、送れる求人が無ければ押させない
              confirmEnabled: () => mailJobIds.length > 0,
              confirmCallback: () => {
                this.doMailNotOverviewSubscribe(
                  mailJobIds.length ? this.sender.mailApiService.bulkRecommend({ ...toSave, jobIds: mailJobIds }) : null,
                );
                this.sender.afterSend({ sendCount: 1 });
                return true;
              },
            });

            // 重複案件は上書きする。履歴書NGの求人だけを除外する
            const overwriteButton = (confirmText: string) => ({
              confirmText: confirmText,
              confirmEnabled: () => hasProgressTarget(resumeOkStudentsWithJobIds) || mailJobIds.length > 0,
              confirmCallback: () => {
                // 「上書き」を選んでも履歴書NGの求人は進捗を進めない
                this.doMailForkJoin(
                  this.sender.model.students
                    .filter((student) => resumeOkStudentsWithJobIds[student.id] && resumeOkStudentsWithJobIds[student.id].length)
                    .map((s) => this.sender.progressApiService.bulkRecommendStudent(s.id, resumeOkStudentsWithJobIds[s.id])),
                  mailJobIds.length
                    ? this.sender.mailApiService
                        .bulkRecommend({ ...toSave, jobIds: mailJobIds })
                        .pipe(tap((result) => this.sender.afterSend(result)))
                    : null,
                );
                this.sender.afterSend({ sendCount: 1 });
                return true;
              },
            });

            // 履歴書NGの求人に加えて重複案件も除外する。重複が無い場合は履歴書NGを除くだけになる
            const excludeButton = (confirmText: string) => ({
              confirmText: confirmText,
              confirmEnabled: () =>
                // disable when all of selected have '進捗重複' or '履歴書未提出' errors
                hasProgressTarget(studentsWithJobIds) || mailJobIdsExcludingDuplicate.length > 0,
              confirmCallback: () => {
                this.doMailForkJoin(
                  this.sender.model.students
                    .filter((student) => studentsWithJobIds[student.id] && studentsWithJobIds[student.id].length)
                    .map((student) => this.sender.progressApiService.bulkRecommendStudent(student.id, studentsWithJobIds[student.id])),
                  mailJobIdsExcludingDuplicate.length
                    ? this.sender.mailApiService
                        .bulkRecommend({ ...toSave, jobIds: mailJobIdsExcludingDuplicate })
                        .pipe(tap((result) => this.sender.afterSend(result)))
                    : null,
                );
                this.sender.afterSend({ sendCount: 1 });
                return true;
              },
              confirmClass: 'btn--green',
            });

            const duplicateTitle = hasResumeNg ? '進捗重複・推薦不可の求人があります' : '進捗重複';
            const title = hasDuplicate ? duplicateTitle : '推薦不可の求人があります';

            PopupControlComponent.instance.open({
              cancelText: '中止',
              cancelCallback: () => this.sender.close(),
              confirmText: null,
              content: this.sender.progressErrorTemplate,
              title: title,
              confirmCallback: null,
              multiConfirm: hasDuplicate
                ? [
                    mailOnlyButton(
                      hasResumeNg ? '重複案件は上書きしないで送信のみ行う（推薦不可は除く）' : '重複案件は上書きしないで送信のみ行う',
                    ),
                    overwriteButton(hasResumeNg ? '重複案件は上書き（推薦不可は除く）' : '重複案件は上書き'),
                    excludeButton(hasResumeNg ? '重複案件と推薦不可を除いて送信' : '重複案件を除いて送信'),
                  ]
                : // 重複が無いときは「上書き」と「重複を除いて送信」が同じ処理になるため、1つのボタンにまとめる
                  [mailOnlyButton('進捗を作成せず送信のみ行う'), excludeButton('推薦不可を除いて送信')],
            });
          } else {
            forkJoin(
              this.sender.model.students.map((s) => this.sender.progressApiService.bulkRecommendStudent(s.id, toSave.jobIds)),
            ).subscribe(() => {
              this.doMailNotOverviewSubscribe(this.sender.mailApiService.bulkRecommend(toSave));
              this.sender.afterSend({ sendCount: 1 });
            });
          }
        });
      } else {
        this.doMailNotOverviewSubscribe(
          this.sender.progressApiService
            .updateWithAction(this.sender.model.progressId, {
              action: this.sender.model.progressStatus === 20 ? 23 : 34,
              type: this.sender.model.progressType,
              hasInterview: this.sender.model.progressHasInterview,
              n: this.sender.model.progressN,
              seminarType: this.sender.model.progressSeminarType,
              seminarAt: this.sender.model.progressSeminarAt,
              seminarAtMailSendFlag: this.sender.model.progressSeminarAtMailSendFlag,
              isStudentDelay: this.sender.model.progressIsStudentDelay,
            })
            .pipe(mergeMap(() => this.sender.mailApiService.bulkRecommend(toSave))),
        );
        this.sender.afterSend({ sendCount: 1 });
      }
    });
  }

  public doMailForkJoin(doForks: any, doMail: any): void {
    forkJoin(doForks).subscribe();
    // 送信対象の求人が1件も無いときは doMail に null が渡る（メールを送らない）
    if (doMail) {
      doMail.subscribe();
    }
  }

  public doMailNotOverviewSubscribe(doMail: any): void {
    if (doMail) {
      doMail.subscribe();
    }
  }
}
